import { subscribeStomp, ensureConnected } from "../stompClient";
import { startJobPolling } from "./AsyncJobService";

// Same silence window as runAsyncJob: if no terminal frame lands within 30s,
// start polling /async-job/{jobId}/status as a WS fallback.
const FRAME_TIMEOUT_MS = 30_000;
const MAX_HELD_PROGRESS_FRAMES = 20;

/**
 * Await the outcome of a single scenario-scoped async job (scope import,
 * breakdown import, schedule update).
 *
 * Ordering guarantee (same as the previous hand-rolled waiters): subscribe →
 * ensureConnected → POST, so an early terminal frame is never missed.
 *
 * On top of the STOMP frame, the 202 body's jobId (AsyncJobStartedDTO) arms a
 * polling fallback on /async-job/{jobId}/status — a lost terminal frame no
 * longer strands the caller until timeoutMs; the polled status funnels into
 * the same onCompleted/onFailed.
 *
 * Frames are additionally discriminated by `jobId`: two concurrent jobs on
 * the SAME scenario share the topic and the itemId, so itemId alone would
 * let the first terminal frame settle whichever waiter subscribed —
 * stranding the other job's UI forever. Terminal frames that arrive before
 * the 202 body (so before our own jobId is known) are held and re-examined
 * once it lands, instead of settling blind.
 *
 * @param {object} config
 *   topic          - per-user STOMP topic to listen on
 *   itemId         - scenarioId; frames with a different itemId are ignored
 *   post           - async () => AxiosResponse — fires the 202 endpoint
 *   timeoutMs      - overall cap before onTimeout fires
 *   onCompleted(frameOrStatus) - terminal COMPLETED/ALL_COMPLETED
 *   onFailed(frameOrStatus)   - terminal FAILED/ALL_FAILED (has .error)
 *   onRequestError(err)       - the POST itself rejected (defaults to onFailed)
 *   onProgress(frame)         - optional: every non-terminal frame of this job (ETA,
 *                               phases); held like terminal frames until the jobId is known
 *   onForbidden()             - optional: the status poll answered 403 (another
 *                               user's job); without it a 403 is retried
 *   pollDelayMs               - silence before polling starts (default 30 s)
 *   timeoutAfterPost          - arm `timeoutMs` once `post` resolved, not at once: for a post
 *                               that may first wait minutes for the scenario lock (it has its
 *                               own budget), so the run itself keeps the whole timeout
 *   onTimeout()
 * @returns cleanup() — settles silently and tears everything down
 */
export function awaitAsyncJob({
  topic,
  itemId,
  post,
  timeoutMs,
  onCompleted,
  onFailed,
  onRequestError = onFailed,
  onForbidden,
  onProgress,
  onTimeout,
  pollDelayMs = FRAME_TIMEOUT_MS,
  timeoutAfterPost = false,
}) {
  const targetIdStr = String(itemId);
  let settled = false;
  let unsubscribe = null;
  let pollingController = null;
  let pollDelayId = null;
  // undefined until the 202 body is parsed; then the backend jobId, or
  // null when the body carried none (sync fallbacks / legacy backends).
  let expectedJobId;
  const heldTerminalFrames = [];
  const heldProgressFrames = [];

  const finish = (fn, arg) => {
    if (settled) return;
    settled = true;
    clearTimeout(timeoutId);
    clearTimeout(pollDelayId);
    if (pollingController) pollingController.abort();
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    fn(arg);
  };

  let timeoutId = null;
  const armTimeout = () => {
    timeoutId = setTimeout(() => finish(onTimeout), timeoutMs);
  };
  if (!timeoutAfterPost) armTimeout();

  const armPollingFallback = (jobId) => {
    // Only poll with a jobId taken from the live 202 body — the status
    // endpoint reports unknown jobs as ALL_FAILED.
    if (!jobId || settled) return;
    pollDelayId = setTimeout(() => {
      if (settled) return;
      pollingController = new AbortController();
      startJobPolling(jobId, pollingController.signal, {
        onAllCompleted: (payload) => finish(onCompleted, payload),
        onAllFailed: (payload) => finish(onFailed, payload),
        onForbidden: onForbidden && (() => finish(onForbidden)),
      });
    }, pollDelayMs);
  };

  const settleOnTerminalFrame = (frame) => {
    if (settled) return;
    if (expectedJobId === undefined) {
      // Our own jobId isn't known yet — this frame could belong to a
      // concurrent job on the same scenario. Hold it until the 202 lands.
      heldTerminalFrames.push(frame);
      return;
    }
    // Once the 202 named our job, a jobId-less terminal frame cannot be
    // attributed to it (LLM signal maps share the topic without a jobId).
    // Without a jobId in the 202 there is nothing to match, so accept it.
    if (expectedJobId && frame.jobId !== expectedJobId) return;
    if (frame.status === "COMPLETED" || frame.status === "ALL_COMPLETED") {
      finish(onCompleted, frame);
    } else {
      finish(onFailed, frame);
    }
  };

  const forwardProgress = (frame) => {
    if (settled) return;
    if (expectedJobId === undefined) {
      // A post that waits for the scenario lock hears the running job's frames for minutes:
      // only the latest few can matter once our own job is named.
      if (heldProgressFrames.length >= MAX_HELD_PROGRESS_FRAMES) heldProgressFrames.shift();
      heldProgressFrames.push(frame);
      return;
    }
    // A progress frame without our jobId cannot be told apart from a concurrent job's.
    if (!expectedJobId || frame.jobId !== expectedJobId) return;
    onProgress(frame);
  };

  // No topic (no signed-in username): status polling alone settles the job.
  const onFrame = (message) => {
    let frame;
    try {
      frame = JSON.parse(message.body);
    } catch {
      return;
    }
    if (frame?.itemId == null || String(frame.itemId) !== targetIdStr) return;
    if (
      frame.status === "COMPLETED" ||
      frame.status === "ALL_COMPLETED" ||
      frame.status === "FAILED" ||
      frame.status === "ALL_FAILED"
    ) {
      settleOnTerminalFrame(frame);
    } else if (onProgress) {
      forwardProgress(frame);
    }
  };
  unsubscribe = topic ? subscribeStomp(topic, onFrame) : () => {};

  (async () => {
    try {
      // Without a topic nothing listens on the socket: polling alone settles the job, and a
      // gateway that is down must not turn the follow into a timeout before it polls once.
      if (topic) await ensureConnected();
      const response = await post();
      if (settled) return;
      if (timeoutAfterPost) armTimeout();
      expectedJobId = response?.data?.jobId ?? null;
      heldProgressFrames.splice(0).forEach(forwardProgress);
      heldTerminalFrames.splice(0).forEach(settleOnTerminalFrame);
      armPollingFallback(expectedJobId);
    } catch (err) {
      finish(onRequestError, err);
    }
  })();

  return () => finish(() => {});
}
